import { BooleanField, TextField } from "@hex-insights/forms";
import { StudentAcademicNoteFormat } from "../../Format";
import { StudentAcademicNoteFormValues } from "../../FormUtilities";
import { BaseFieldProps } from "../shared";
import { StudentNoteSelectField } from "../StudentNote";

/**
 * Generic props for fields of the StudentAcademicNote model.
 */
type FieldProps<K extends keyof StudentAcademicNoteFormValues.Base = keyof StudentAcademicNoteFormValues.Base> =
	BaseFieldProps<Pick<StudentAcademicNoteFormValues.Base, K>>;

/**
 * Field component for the `isIssueNatureLearningDifficulty` field of the StudentAcademicNote model.
 */
export function IsIssueNatureLearningDifficulty({
	formState,
	label = StudentAcademicNoteFormat.fieldLabels.isIssueNatureLearningDifficulty,
	...props
}: FieldProps<"isIssueNatureLearningDifficulty">) {
	return <BooleanField formState={formState} name="isIssueNatureLearningDifficulty" label={label} {...props} />;
}

/**
 * Field component for the `isIssueNatureAttentionIssue` field of the StudentAcademicNote model.
 */
export function IsIssueNatureAttentionIssue({
	formState,
	label = StudentAcademicNoteFormat.fieldLabels.isIssueNatureAttentionIssue,
	...props
}: FieldProps<"isIssueNatureAttentionIssue">) {
	return <BooleanField formState={formState} name="isIssueNatureAttentionIssue" label={label} {...props} />;
}

/**
 * Field component for the `isIssueNatureFocusIssue` field of the StudentAcademicNote model.
 */
export function IsIssueNatureFocusIssue({
	formState,
	label = StudentAcademicNoteFormat.fieldLabels.isIssueNatureFocusIssue,
	...props
}: FieldProps<"isIssueNatureFocusIssue">) {
	return <BooleanField formState={formState} name="isIssueNatureFocusIssue" label={label} {...props} />;
}

/**
 * Field component for the `isIssueNatureEmotionalOrVulnerableIssue` field of the StudentAcademicNote model.
 */
export function IsIssueNatureEmotionalOrVulnerableIssue({
	formState,
	label = StudentAcademicNoteFormat.fieldLabels.isIssueNatureEmotionalOrVulnerableIssue,
	...props
}: FieldProps<"isIssueNatureEmotionalOrVulnerableIssue">) {
	return <BooleanField formState={formState} name="isIssueNatureEmotionalOrVulnerableIssue" label={label} {...props} />;
}

/**
 * Field component for the `isIssueNatureOther` field of the StudentAcademicNote model.
 */
export function IsIssueNatureOther({
	formState,
	label = StudentAcademicNoteFormat.fieldLabels.isIssueNatureOther,
	...props
}: FieldProps<"isIssueNatureOther">) {
	return <BooleanField formState={formState} name="isIssueNatureOther" label={label} {...props} />;
}

/**
 * Field component for the `issueNatureOther` field of the StudentAcademicNote model.
 */
export function IssueNatureOther({
	formState,
	label = StudentAcademicNoteFormat.fieldLabels.issueNatureOther,
	...props
}: FieldProps<"issueNatureOther">) {
	return <TextField formState={formState} name="issueNatureOther" optional multiLine label={label} {...props} />;
}

/**
 * Field component for the `isSolutionRecDifferentiatedLearning` field of the StudentAcademicNote model.
 */
export function IsSolutionRecDifferentiatedLearning({
	formState,
	label = StudentAcademicNoteFormat.fieldLabels.isSolutionRecDifferentiatedLearning,
	...props
}: FieldProps<"isSolutionRecDifferentiatedLearning">) {
	return <BooleanField formState={formState} name="isSolutionRecDifferentiatedLearning" label={label} {...props} />;
}

/**
 * Field component for the `isSolutionRecIndividualLearningPlan` field of the StudentAcademicNote model.
 */
export function IsSolutionRecIndividualLearningPlan({
	formState,
	label = StudentAcademicNoteFormat.fieldLabels.isSolutionRecIndividualLearningPlan,
	...props
}: FieldProps<"isSolutionRecIndividualLearningPlan">) {
	return <BooleanField formState={formState} name="isSolutionRecIndividualLearningPlan" label={label} {...props} />;
}

/**
 * Field component for the `isSolutionRecTutoring` field of the StudentAcademicNote model.
 */
export function IsSolutionRecTutoring({
	formState,
	label = StudentAcademicNoteFormat.fieldLabels.isSolutionRecTutoring,
	...props
}: FieldProps<"isSolutionRecTutoring">) {
	return <BooleanField formState={formState} name="isSolutionRecTutoring" label={label} {...props} />;
}

/**
 * Field component for the `isSolutionRecProfessionalSupport` field of the StudentAcademicNote model.
 */
export function IsSolutionRecProfessionalSupport({
	formState,
	label = StudentAcademicNoteFormat.fieldLabels.isSolutionRecProfessionalSupport,
	...props
}: FieldProps<"isSolutionRecProfessionalSupport">) {
	return <BooleanField formState={formState} name="isSolutionRecProfessionalSupport" label={label} {...props} />;
}

/**
 * Field component for the `isSolutionRecInClassSupport` field of the StudentAcademicNote model.
 */
export function IsSolutionRecInClassSupport({
	formState,
	label = StudentAcademicNoteFormat.fieldLabels.isSolutionRecInClassSupport,
	...props
}: FieldProps<"isSolutionRecInClassSupport">) {
	return <BooleanField formState={formState} name="isSolutionRecInClassSupport" label={label} {...props} />;
}

/**
 * Field component for the `isSolutionRecOther` field of the StudentAcademicNote model.
 */
export function IsSolutionRecOther({
	formState,
	label = StudentAcademicNoteFormat.fieldLabels.isSolutionRecOther,
	...props
}: FieldProps<"isSolutionRecOther">) {
	return <BooleanField formState={formState} name="isSolutionRecOther" label={label} {...props} />;
}

/**
 * Field component for the `solutionRecOther` field of the StudentAcademicNote model.
 */
export function SolutionRecOther({
	formState,
	label = StudentAcademicNoteFormat.fieldLabels.solutionRecOther,
	...props
}: FieldProps<"solutionRecOther">) {
	return <TextField formState={formState} name="solutionRecOther" optional multiLine label={label} {...props} />;
}

export namespace StudentNote {
	export type SelectProps = FieldProps<"studentNoteID"> &
		Omit<StudentNoteSelectField.SelectProps<StudentAcademicNoteFormValues.Base>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `studentNote` edge of the StudentAcademicNote model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <StudentNoteSelectField.Select formState={formState} name="studentNoteID" {...props} />;
	}
}
